import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { TennisBall, Ticket, SignOut } from '@phosphor-icons/react';
import { captureInviteCode, clearPendingInviteCode } from '../lib/inviteCode';
import rcMark from '../assets/rc-mark.png';

export default function InviteRedemptionPage() {
  const { user, hasLadderAccess, login, logout } = useAuth();
  const [code, setCode] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const captured = captureInviteCode(location.search);
    if (captured) {
      setCode(captured);
      setPrefilled(true);
    }
  }, [location.search]);

  // Not logged in → go to login (preserve code so login can re-apply it)
  if (!user) {
    const target = code ? `/login?code=${code}` : '/login';
    return <Navigate to={target} replace />;
  }

  // Already has ladder access → go to app
  if (hasLadderAccess) {
    return <Navigate to="/" replace />;
  }

  const handleRedeem = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Please enter your invite code.');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.redeemInvite(code.trim().toUpperCase());
      login(res.data.user, res.data.player, res.data.has_ladder_access, res.data.profile_completed);
      clearPendingInviteCode();
      // After invite redemption, go to profile completion
      navigate('/complete-profile', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to redeem invite code.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fbfaf8]">
      {/* Minimal header */}
      <header className="px-6 py-4 flex justify-between items-center border-b border-[#e3dcd1]">
        <div className="flex items-center gap-2">
          <img src={rcMark} alt="The Rippers Club" className="w-8 h-8 object-contain" />
          <span className="font-serif text-lg text-[#013523] tracking-tight">The Rippers Club</span>
        </div>
        <button
          data-testid="invite-page-logout"
          onClick={handleLogout}
          className="text-[#6a766e] hover:text-[#013523] transition-colors flex items-center gap-1.5 text-sm"
        >
          <SignOut size={18} />
          Sign out
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-6 animate-fade-in-up" data-testid="invite-redemption-page">
          {/* Icon */}
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#015e3f]/10 mx-auto flex items-center justify-center mb-4">
              <Ticket size={32} weight="duotone" className="text-[#015e3f]" />
            </div>
            <h1 className="font-serif text-3xl text-[#013523] tracking-tight">Enter Invite Code</h1>
            <p className="text-sm text-[#6a766e] mt-2 leading-relaxed">
              Welcome, <span className="font-medium text-[#013523]">{user.name}</span>. This is a private ladder.
              Enter the invite code you received to join.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div data-testid="invite-error" className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 border border-red-200">
              {error}
            </div>
          )}

          {prefilled && !error && (
            <div
              data-testid="invite-prefilled-banner"
              className="bg-[#015e3f]/10 text-[#015e3f] text-xs rounded-xl px-4 py-2.5 border border-[#015e3f]/20 flex items-center gap-2"
            >
              <Ticket size={14} weight="duotone" />
              <span>Invite code applied from your link. Tap Join to continue.</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleRedeem} className="space-y-4">
            <div>
              <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Invite Code</label>
              <input
                data-testid="invite-code-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-4 text-[#2a332c] text-center font-mono text-xl tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all placeholder:text-[#e3dcd1] placeholder:tracking-[0.3em]"
                placeholder="ABCD1234"
                maxLength={12}
                autoFocus={!prefilled}
              />
            </div>
            <button
              data-testid="redeem-invite-button"
              type="submit"
              disabled={loading || !code.trim()}
              className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3.5 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50"
            >
              {loading ? 'Joining...' : 'Join The Rippers Club'}
            </button>
          </form>

          {/* Help text */}
          <div className="bg-[#f0ece3] rounded-2xl border border-[#e3dcd1] p-4">
            <p className="text-xs text-[#6a766e] leading-relaxed">
              Don't have an invite code? Ask the ladder administrator to send you one. Invites are required to maintain the integrity of the ranking system.
            </p>
          </div>

          {/* Signed in as */}
          <div className="text-center">
            <p className="text-xs text-[#6a766e]">
              Signed in as <span className="font-medium text-[#013523]">{user.email}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
